import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppSelector, selectAccount } from "@redux";

/**
 * Custom hook to extract the shared_by_email from various quote sources based on context.
 *
 * This helper is necessary because the shared_by_email can come from different places
 * depending on the application state and user permissions:
 *
 * 1. **Pending Quotes (isPending=true)**: Uses sharedQuotes array with 'shared_by' field
 *    - These are quotes that haven't been accepted/declined yet
 *    - Data structure: { shared_by: string }
 *
 * 2. **Regular Quotes with Order Tab Enabled**: Searches in allQuotes and myQuotes with 'shared_by_email' field
 *    - Users with order tab permissions have access to broader quote collections
 *    - Data structure: { shared_by_email: string }
 *
 * 3. **Regular Quotes without Order Tab**: Uses quotes array with 'shared_by_email' field
 *    - Standard users have limited quote access
 *    - Data structure: { shared_by_email: string }
 *
 */
export const useSharedByEmail = (): string | null => {
    const [searchParams] = useSearchParams();
    const { quoteId: quoteIdFromParam } = useParams<{ quoteId: string }>();
    const quoteId = quoteIdFromParam || searchParams.get("quoteId");
    const isPending = searchParams.get("isPending") === "true";
    const { metadata } = useAppSelector(selectAccount);
    const sharedQuotes = useAppSelector((state) => state.allQuotes.sharedQuotes);
    const allQuotes = useAppSelector((state) => state.allQuotes.allQuotes);
    const myQuotes = useAppSelector((state) => state.myQuotes.quotes);
    const quotes = useAppSelector((state) => state.allQuotes.quotes);
    const isOrderTabEnabled = Boolean(metadata?.is_order_tab_enabled);

    return useMemo(() => {
        if (!quoteId) return null;

        // For pending quotes, use sharedQuotes array with 'shared_by' field
        if (isPending) {
            return sharedQuotes.find((q) => q.id === parseInt(quoteId))?.shared_by || null;
        }

        // For accepted quotes, check different sources based on user permissions
        const email = isOrderTabEnabled
            ? // Users with order tab can access both allQuotes and myQuotes
              (
                  allQuotes?.find((quote) => quote.id === parseInt(quoteId)) ||
                  myQuotes?.find((quote) => quote.id === parseInt(quoteId))
              )?.shared_by_email
            : // Standard users only have access to quotes array
              quotes?.find((quote) => quote.id === parseInt(quoteId))?.shared_by_email;

        return email || null;
    }, [quoteId, isPending, isOrderTabEnabled, allQuotes, myQuotes, quotes, sharedQuotes]);
};
